import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";

import { clientTimeZone } from "./dateLocal";
import { isAuthoritativeTripQuote } from "./bookingQuoteDisplay";

const ONE_WAY_DEBOUNCE_MS = 450;

/** Server-side trip pricing — matches calendar per-day rates and regional taxes/fees. */
export function useTripQuote(
  vehicleId,
  startDate,
  endDate,
  {
    enabled = true,
    pickupLocation = "",
    dropoffLocation = "",
    dropoffPostal = "",
    oneWay = false,
    redeemZct = 0,
    couponCode = "",
  } = {},
) {
  const [result, setResult] = useState({
    requestKey: null,
    quote: null,
    loading: false,
    quoteError: null,
  });
  const debounceRef = useRef(null);
  const aliveRef = useRef(true);
  const tz = clientTimeZone();
  const requestKey = enabled && vehicleId && startDate && endDate
    ? JSON.stringify([
      vehicleId, startDate, endDate, tz, pickupLocation.trim(),
      oneWay, dropoffLocation.trim(), dropoffPostal.trim(),
      Number(redeemZct) || 0, couponCode.trim().toUpperCase(),
    ])
    : null;

  useEffect(() => {
    clearTimeout(debounceRef.current);
    aliveRef.current = false;

    if (!requestKey) {
      setResult({ requestKey: null, quote: null, loading: false, quoteError: null });
      return;
    }

    const needsDebounce = oneWay && (dropoffLocation.trim() || dropoffPostal.trim());

    if (needsDebounce) {
      setResult((prev) => ({ ...prev, loading: true, quoteError: null }));
      debounceRef.current = setTimeout(() => fetchQuote(requestKey), ONE_WAY_DEBOUNCE_MS);
    } else {
      fetchQuote(requestKey);
    }

    return () => {
      clearTimeout(debounceRef.current);
      aliveRef.current = false;
    };

    function fetchQuote(key) {
      aliveRef.current = true;
      const alive = aliveRef;
      setResult({ requestKey: key, quote: null, loading: true, quoteError: null });
      (async () => {
        try {
          const params = { start_date: startDate, end_date: endDate, client_tz: tz };
          if (pickupLocation?.trim()) params.pickup_location = pickupLocation.trim();
          if (oneWay) {
            params.one_way = true;
            if (dropoffLocation?.trim()) params.dropoff_location = dropoffLocation.trim();
            if (dropoffPostal?.trim()) params.dropoff_postal = dropoffPostal.trim();
          }
          if (redeemZct > 0) params.redeem_zct = redeemZct;
          if (couponCode?.trim()) params.coupon_code = couponCode.trim().toUpperCase();
          const r = await axios.get(`${API}/vehicles/${vehicleId}/booking-quote`, {
            params,
            withCredentials: true,
          });
          if (alive.current) {
            setResult({ requestKey: key, quote: r.data, loading: false, quoteError: null });
          }
        } catch (e) {
          if (alive.current) {
            setResult({
              requestKey: key,
              quote: null,
              loading: false,
              quoteError: e.response?.data?.detail || "quote_unavailable",
            });
          }
        }
      })();
    }
  }, [requestKey]); // eslint-disable-line react-hooks/exhaustive-deps -- key contains every pricing input

  const isCurrent = !!requestKey && result.requestKey === requestKey;
  const loading = !!requestKey && (!isCurrent || result.loading);
  const quote = isCurrent && !result.loading ? result.quote : null;
  const quoteError = isCurrent ? result.quoteError : null;
  const quoteValid = isAuthoritativeTripQuote(quote) && !loading && !quoteError;

  return { quote, loading, quoteError, quoteValid };
}
